import React from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

type SectionProps = React.PropsWithChildren<{ title: string }>;
const Section: React.FC<SectionProps> = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
      {title}
    </Typography>
    <Divider sx={{ mb: 2 }} />
    <Box>{children}</Box>
  </Box>
);

export const AccuLynxHelp: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ p: 3, mt: 10, width: '100%' }}>
      <Box sx={{ maxWidth: '900px', mr: 'auto', ml: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            AccuLynx Integration Help
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/settings/integrations/acculynx/setup')}
          >
            Back to Setup
          </Button>
        </Box>

        <Typography color="text.secondary" sx={{ mb: 4 }}>
          Need assistance? Contact{' '}
          <Link href="mailto:support@paiv.ai">support@paiv.ai</Link>.
        </Typography>

        <Section title="1. Connect AccuLynx">
          <List dense>
            <ListItem>
              <ListItemText primary="Enter your AccuLynx API key on the FieldInspect AccuLynx setup page." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Choose how you want to map your AccuLynx stages to FieldInspect stages. You can choose to map your acculynx locations by Company, Division, or Team." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Save the integration to begin syncing." />
            </ListItem>
          </List>
        </Section>

        <Section title="2. Stage Mapping Guidance">
          <List dense>
            <ListItem>
              <ListItemText primary="Map each AccuLynx stage to a corresponding FieldInspect stage for consistent syncing." />
            </ListItem>
            <ListItem>
              <ListItemText primary="You can edit mappings anytime; changes take effect on the next sync." />
            </ListItem>
          </List>
        </Section>

        <Section title="Troubleshooting">
          <List dense>
            <ListItem>
              <ListItemText
                primary="Authentication Errors"
                secondary="Verify your credentials and permissions in AccuLynx."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Missing Stages"
                secondary="Refresh stages and ensure your FieldInspect account has access to the pipeline you expect to sync."
              />
            </ListItem>
          </List>
        </Section>
      </Box>
    </Box>
  );
};

export default AccuLynxHelp;
